import { useEffect, useMemo, useRef, useState } from "react";
import { createNotificationsHub, } from "../../infrastructure/notifications-hub";
import { setNotificationsHubApi } from "./notificationsHubApiStore";
export function useNotificationsHub(apiBase, getToken, enabled) {
    const [bootstrap, setBootstrap] = useState(null);
    const [status, setStatus] = useState("idle");
    const [unreadCount, setUnreadCount] = useState(0);
    const apiRef = useRef(null);
    const connectionRef = useRef(null);
    const handlersRef = useRef({});
    const api = useMemo(() => ({
        markAsRead: async (id) => {
            if (!apiRef.current)
                return;
            await apiRef.current.markAsRead(id);
        },
        markAsUnread: async (id) => {
            if (!apiRef.current)
                return;
            await apiRef.current.markAsUnread(id);
        },
        markAllAsRead: async () => {
            if (!apiRef.current)
                return;
            await apiRef.current.markAllAsRead();
        },
        stop: async () => {
            if (!apiRef.current)
                return;
            await apiRef.current.stop();
        },
    }), []);
    useEffect(() => {
        let cancelled = false;
        async function init() {
            try {
                if (!enabled) {
                    return;
                }
                const token = getToken?.();
                if (!token) {
                    return;
                }
                setStatus("connecting");
                const hub = await createNotificationsHub({
                    apiBase,
                    getToken,
                    callbacks: {
                        onNotificationsUpdated: () => {
                            handlersRef.current.onNotificationsUpdated?.();
                        },
                        onUnreadCountUpdated: (count) => {
                            setUnreadCount(count);
                        },
                        onNotificationRead: (id) => {
                            handlersRef.current.onNotificationRead?.(id);
                        },
                        onNotificationsMarkedAllRead: (ids) => {
                            handlersRef.current.onNotificationsMarkedAllRead?.(ids);
                        },
                    },
                    shouldCancel: () => cancelled,
                });
                if (cancelled) {
                    // If unmounted/disabled during bootstrap, stop connection immediately.
                    await hub.api.stop();
                    return;
                }
                connectionRef.current = Promise.resolve(hub);
                apiRef.current = hub.api;
                setNotificationsHubApi(hub.api);
                setBootstrap(hub.bootstrap);
                setStatus("connected");
                // Track connection lifecycle for UI status
                hub.connection.onreconnecting(() => {
                    setStatus("reconnecting");
                });
                hub.connection.onreconnected(() => {
                    setStatus("connected");
                });
                hub.connection.onclose(() => {
                    setStatus("disconnected");
                });
            }
            catch (err) {
                if (!cancelled) {
                    setStatus("error");
                }
            }
        }
        init();
        return () => {
            cancelled = true;
            setBootstrap(null);
            setUnreadCount(0);
            if (apiRef.current) {
                apiRef.current.stop().catch(() => void 0);
            }
            setNotificationsHubApi(null);
            apiRef.current = null;
            connectionRef.current = null;
            setStatus("idle");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiBase, enabled]);
    const setHandlers = (h) => {
        handlersRef.current = h || {};
    };
    return {
        bootstrap,
        connectionStatus: status,
        unreadCount,
        api,
        setHandlers,
    };
}
